// frontend/src/components/UI/PasswordField.js
import React, { forwardRef, useState } from 'react';
import { CheckCircle, Eye, EyeOff, Lock, XCircle } from 'lucide-react';
import {
  getPasswordRequirementChecks,
  PASSWORD_REQUIREMENT_ITEMS,
} from '../../utils/passwordPolicy';

const STRENGTH = {
  0: { label: 'Weak', text: 'text-red-600', bar: 'bg-red-500', width: 'w-0' },
  1: { label: 'Weak', text: 'text-red-600', bar: 'bg-red-500', width: 'w-1/5' },
  2: { label: 'Weak', text: 'text-red-600', bar: 'bg-red-500', width: 'w-2/5' },
  3: { label: 'Fair', text: 'text-amber-700', bar: 'bg-amber-500', width: 'w-3/5' },
  4: { label: 'Good', text: 'text-blue-700', bar: 'bg-blue-600', width: 'w-4/5' },
  5: { label: 'Strong', text: 'text-green-700', bar: 'bg-green-600', width: 'w-full' },
};

const Requirement = ({ met, text }) => (
  <li className="flex items-center gap-1.5">
    {met ? (
      <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" aria-hidden="true" />
    ) : (
      <XCircle className="w-3.5 h-3.5 text-gray-300 shrink-0" aria-hidden="true" />
    )}
    <span className={`text-xs ${met ? 'text-green-700' : 'text-gray-500'}`}>
      <span className="sr-only">{met ? 'Met: ' : 'Not met: '}</span>
      {text}
    </span>
  </li>
);

export function PasswordRequirements({ password }) {
  const checks = getPasswordRequirementChecks(password);
  const score = PASSWORD_REQUIREMENT_ITEMS.filter((item) => checks[item.key]).length;
  const strength = STRENGTH[score] || STRENGTH[0];

  return (
    <div className="mt-2">
      <div className="mb-1.5 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
          <div className={`h-full transition-all duration-200 ${strength.bar} ${strength.width}`} />
        </div>
        <span className={`w-12 text-xs font-medium ${strength.text}`}>{strength.label}</span>
      </div>
      <ul className="space-y-1" aria-label="Password requirements">
        {PASSWORD_REQUIREMENT_ITEMS.map((item) => (
          <Requirement key={item.key} met={Boolean(checks[item.key])} text={item.text} />
        ))}
      </ul>
    </div>
  );
}

const PasswordField = forwardRef(function PasswordField({
  id,
  label,
  helper,
  error,
  required = false,
  ...inputProps
}, ref) {
  const [visible, setVisible] = useState(false);
  const errorId = error ? `${id}-error` : undefined;
  const helperId = helper ? `${id}-helper` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </label>
      {helper && (
        <p id={helperId} className="mb-2 text-xs text-gray-500">
          {helper}
        </p>
      )}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Lock className="h-4 w-4 text-gray-400" aria-hidden="true" />
        </div>
        <input
          {...inputProps}
          id={id}
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={`input pl-10 pr-12 ${error ? 'input-error' : ''}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          required={required || undefined}
        />
        <button
          type="button"
          className="icon-button icon-button-sm absolute right-1.5 top-1/2 -translate-y-1/2"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-xs text-error-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

export default PasswordField;
